import React, { useState, useRef, useEffect } from 'react';
import '../styles/MobileControls.css';
import { blockToColour } from '../../scripts/blocks';

interface MobileControlsProps {
    player: any; // Using any for now to avoid complex type issues with the JS class
    onOpenMenu: () => void;
}

const MobileControls: React.FC<MobileControlsProps> = ({ player, onOpenMenu }) => {
    const [isSprinting, setIsSprinting] = useState(false);
    const [activeBlock, setActiveBlock] = useState(0);
    const joystickRef = useRef<HTMLDivElement>(null);
    const stickRef = useRef<HTMLDivElement>(null);
    const [stickPos, setStickPos] = useState({ x: 0, y: 0 });
    const isSprintingRef = useRef(false);

    useEffect(() => {
        if (player) {
            setActiveBlock(player.activeBlockId);
        }
    }, [player]);

    const handleJoystickMove = (e: React.TouchEvent) => {
        if (!joystickRef.current || !player || !player.touchControls) return;

        const rect = joystickRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const touch = e.touches[0];
        let dx = touch.clientX - centerX;
        let dy = touch.clientY - centerY;

        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxRadius = rect.width / 2;

        if (distance > maxRadius) {
            dx *= maxRadius / distance;
            dy *= maxRadius / distance;
        }

        setStickPos({ x: dx, y: dy });

        // Normalize values between -1 and 1
        const normX = dx / maxRadius;
        const normY = dy / maxRadius;

        player.touchControls.setMovement(-normY, normX);
    };

    const handleJoystickEnd = () => {
        setStickPos({ x: 0, y: 0 });
        if (player && player.touchControls) {
            player.touchControls.setMovement(0, 0);
        }
    };

    const toggleSprint = () => {
        const newVal = !isSprinting;
        setIsSprinting(newVal);
        isSprintingRef.current = newVal;
        if (player && player.touchControls) {
            player.touchControls.setSprint(newVal);
        }
    };

    const jump = () => {
        if (player && player.touchControls) {
            player.touchControls.triggerJump(true);
        }
    };

    const performAction = () => {
        if (player) {
            player.performAction();
        }
    };

    const selectBlock = (id: number) => {
        if (player) {
            player.activeBlockId = id;
            setActiveBlock(id);
        }
    };

    const handleExit = () => {
        if (player) {
            player.onCameraUnlock();
        }
        onOpenMenu();
    };

    return (
        <div className="mobile-controls-container">
            <div
                className="joystick-zone"
                onTouchMove={handleJoystickMove}
                onTouchEnd={handleJoystickEnd}
                onTouchCancel={handleJoystickEnd}
            >
                <div className="joystick-base" ref={joystickRef}>
                    <div
                        className="joystick-stick"
                        ref={stickRef}
                        style={{
                            transform: `translate(calc(-50% + ${stickPos.x}px), calc(-50% + ${stickPos.y}px))`
                        }}
                    />
                </div>
            </div>

            <div className="action-buttons-zone">
                <div className={`mobile-btn ${isSprinting ? 'active' : ''}`} onPointerDown={toggleSprint}>
                    ⚡
                </div>
                <div className="mobile-btn" onPointerDown={jump}>
                    ⤴️
                </div>
                <div className="mobile-btn action-main" onPointerDown={performAction}>
                    🔨
                </div>
            </div>

            <div className="mobile-block-selector">
                {Object.entries(blockToColour).map(([_, color], index) => {
                    const id = index + 1;
                    return (
                        <div
                            key={id}
                            className={`mobile-block-item ${activeBlock === id ? 'selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onPointerDown={() => selectBlock(id)}
                        />
                    );
                })}
                <div
                    className={`mobile-block-item ${activeBlock === 0 ? 'selected' : ''}`}
                    style={{ backgroundImage: 'url(/textures/pickaxe.png)', backgroundSize: 'cover' }}
                    onPointerDown={() => selectBlock(0)}
                />
            </div>

            <div className="exit-btn" onPointerDown={handleExit}>
                ☰
            </div>
        </div>
    );
};

export default MobileControls;
